import { useEffect, useState } from 'react';
import { Box, Checkbox, Image, List, Loader, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconCheck } from '@tabler/icons-react';
import { cuvinteDinRezultat, evalueazaDocument, stergeLinii } from '../utils/verificareDocument';

// bibliotecile sunt incarcate doar cand e nevoie, ca sa nu mareasca pagina principala
const incarcaPdfjs = async () => {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
  // worker-ul ruleaza in pagina (fara fisier separat de pe un CDN)
  await import('pdfjs-dist/legacy/build/pdf.worker.entry');
  return pdfjs;
};

const randeazaPagina = async (pdf, numar, scale) => {
  const pagina = await pdf.getPage(numar);
  const viewport = pagina.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  await pagina.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
  return canvas;
};

// previzualizarea foloseste o rezolutie mai mica; pentru OCR prima pagina se randeaza
// la rezolutie mare, altfel textul subliniat (titlul, numele profesorului) nu e citit corect
const randeazaPagini = async (fisier) => {
  const pdfjs = await incarcaPdfjs();
  const pdf = await pdfjs.getDocument({ data: await fisier.arrayBuffer() }).promise;
  const pagini = [];
  for (let i = 1; i <= Math.min(pdf.numPages, 3); i++) {
    pagini.push((await randeazaPagina(pdf, i, 1.5)).toDataURL('image/jpeg', 0.85));
  }
  const pentruOcr = await randeazaPagina(pdf, 1, 3);
  return { pagini, pentruOcr, numarPagini: pdf.numPages };
};

// copie a paginii fara sublinieri, data OCR-ului
const faraSublinieri = (canvas, imagine) => {
  const copie = document.createElement('canvas');
  copie.width = canvas.width;
  copie.height = canvas.height;
  const curat = new ImageData(stergeLinii(imagine.data, imagine.width, imagine.height), imagine.width, imagine.height);
  copie.getContext('2d').putImageData(curat, 0, 0);
  return copie;
};

/**
 * Previzualizarea documentului incarcat, verificarea automata (OCR) si confirmarea utilizatorului.
 * Verificarea automata doar avertizeaza; incarcarea depinde de confirmarea bifata.
 */
function VerificareDocument({ fisier, student, profesor, titlu, rol, intrebare, onConfirmare }) {
  const [pagini, setPagini] = useState([]);
  const [numarPagini, setNumarPagini] = useState(0);
  const [stare, setStare] = useState('incarcare');
  const [rezultate, setRezultate] = useState([]);
  const [confirmat, setConfirmat] = useState(false);

  useEffect(() => {
    let anulat = false;
    let worker;
    setPagini([]);
    setRezultate([]);
    setConfirmat(false);
    onConfirmare(false);
    if (!fisier) return;

    const verifica = async () => {
      let pentruOcr;
      try {
        setStare('incarcare');
        const randat = await randeazaPagini(fisier);
        pentruOcr = randat.pentruOcr;
        if (anulat) return;
        setPagini(randat.pagini);
        setNumarPagini(randat.numarPagini);
      } catch (e) {
        console.error(e);
        if (!anulat) setStare('fisier-invalid');
        return;
      }

      try {
        setStare('verificare');
        const { createWorker } = await import('tesseract.js');
        worker = await createWorker('ron');
        // semnaturile se cauta pe imaginea originala, textul pe copia fara sublinieri
        const imagine = pentruOcr.getContext('2d').getImageData(0, 0, pentruOcr.width, pentruOcr.height);
        const { data } = await worker.recognize(faraSublinieri(pentruOcr, imagine));
        if (anulat) return;
        setRezultate(
          evalueazaDocument({
            text: data.text,
            cuvinteOcr: cuvinteDinRezultat(data),
            imagine,
            student,
            profesor,
            titlu,
            rol,
          })
        );
        setStare('gata');
      } catch (e) {
        console.error(e);
        if (!anulat) setStare('ocr-indisponibil');
      } finally {
        if (worker) worker.terminate();
      }
    };
    verifica();

    return () => {
      anulat = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fisier]);

  if (!fisier) return null;

  if (stare === 'fisier-invalid') {
    return (
      <Text c="red" mt="md">
        Fișierul nu a putut fi deschis. Verifică dacă este un PDF valid și încearcă din nou.
      </Text>
    );
  }

  const avertismente = rezultate.filter((r) => !r.ok).length;

  return (
    <Stack mt="md" gap="sm" align="stretch" style={{ textAlign: 'left' }}>
      <Text fw={600}>Previzualizare document{numarPagini > 3 ? ` (primele 3 din ${numarPagini} pagini)` : ''}</Text>
      <Box style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid #ced4da', borderRadius: 4, background: '#f1f3f5' }}>
        {pagini.length === 0 ? (
          <Box p="md" style={{ display: 'flex', justifyContent: 'center' }}><Loader size="sm" /></Box>
        ) : (
          pagini.map((src, i) => <Image key={i} src={src} alt={`Pagina ${i + 1}`} mb={4} />)
        )}
      </Box>

      <Text fw={600}>Verificare automată</Text>
      {(stare === 'incarcare' || stare === 'verificare') && (
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Loader size="xs" />
          <Text size="sm">Se verifică documentul (poate dura câteva secunde)...</Text>
        </Box>
      )}
      {stare === 'ocr-indisponibil' && (
        <Text size="sm" c="orange.8">
          Verificarea automată nu a putut rula. Verifică manual datele în previzualizare.
        </Text>
      )}
      {stare === 'gata' && (
        <>
          <List spacing={4} size="sm" center>
            {rezultate.map((r, i) => (
              <List.Item key={i} icon={
                <ThemeIcon color={r.ok ? 'teal' : 'orange'} size={20} radius="xl">
                  {r.ok ? <IconCheck size={14} /> : <IconAlertTriangle size={14} />}
                </ThemeIcon>
              }>{r.mesaj}</List.Item>
            ))}
          </List>
          <Text size="xs" c="dimmed">
            {avertismente > 0
              ? 'Verificarea automată poate greși la documentele scanate. Verifică punctele marcate în previzualizare.'
              : 'Verificarea automată este orientativă. Uită-te totuși peste previzualizare.'}
          </Text>
        </>
      )}

      <Checkbox
        mt="sm"
        checked={confirmat}
        disabled={pagini.length === 0}
        onChange={(e) => {
          setConfirmat(e.currentTarget.checked);
          onConfirmare(e.currentTarget.checked);
        }}
        label={<Text fw={600} size="sm">{intrebare}</Text>}
      />
    </Stack>
  );
}

export default VerificareDocument;
